import type { ExplicitContextItem } from '@protoss/shared';
import { Button, IconSize, ReloadIcon, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { useLayout } from '../../../contexts/LayoutContext';
import { useManualSync } from '../hooks/useManualSync';
import { useSourceSystem } from '../hooks/useSourceSystem';
import type { SourceType } from '../types/source-system';
import { PipelineBrowser } from './PipelineBrowser';
import { SourceSystemDataSection } from './SourceSystemDataSection';
import { SourceSystemLogsSection } from './SourceSystemLogsSection';
import { SourceSystemRepoBrowser } from './SourceSystemRepoBrowser';

const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  repo: 'Repositories',
  board: 'Boards',
  pipeline: 'Pipelines',
  'data-catalog': 'Data Catalog',
};

interface SourceSystemPanelProps {
  projectUuid: string;
  sourceType: SourceType;
  /** "Domain Name > Project Name" - provided by the page */
  breadcrumb: string;
  /** True when the current user can manage integrations (Admin or Manager) */
  canEdit: boolean;
  /**
   * Increment this value to trigger a re-fetch of panel state.
   * Used by parent page when settings are saved from the right pane.
   */
  refreshKey?: number;
  /**
   * Called whenever the most-relevant job run changes.
   * Provides the job run UUID to show in the right-pane Sync Log tab.
   * `isActive` is true while the run is pending or running.
   */
  onRunStateChange?: (runUuid: string | null, isActive: boolean) => void;
  /**
   * Called when the user explicitly selects a node (file/directory) in the
   * repo browser or adds a Monaco line-range to AI context.
   * Only passed through to the repo browser; ignored for boards/pipelines.
   */
  onSetDraftExplicitContext?: (item: ExplicitContextItem) => void;
}

/**
 * Source system panel.
 *
 * Owns its own header (title + breadcrumb + Settings + Refresh actions).
 * Repos use a repository browser main pane.
 * Boards and pipelines keep the observability-first body.
 * Settings surface lives in the right-pane via SourceSystemSettingsPane.
 */
export function SourceSystemPanel({
  projectUuid,
  sourceType,
  breadcrumb,
  canEdit,
  refreshKey,
  onRunStateChange,
  onSetDraftExplicitContext,
}: SourceSystemPanelProps) {
  const { state, loading, error, refresh, hasActiveRun } = useSourceSystem(projectUuid, sourceType);
  const syncScope =
    sourceType === 'repo' ? 'repos' : sourceType === 'board' ? 'boards' : 'pipelines';
  const { state: syncState, trigger } = useManualSync(
    projectUuid,
    sourceType,
    syncScope as 'repos' | 'boards' | 'pipelines'
  );
  const { setRightPaneVisible, setRightPaneMode } = useLayout();

  // Notify parent of the current run UUID so it can drive the Sync Log right pane.
  React.useEffect(() => {
    if (!onRunStateChange) return;
    const activeUuid = state?.activeRun?.jobRunUuid ?? null;
    const lastUuid = state?.lastRun?.jobRunUuid ?? null;
    const currentRunUuid = activeUuid ?? lastUuid;
    const isActive = activeUuid !== null;
    onRunStateChange(currentRunUuid, isActive);
  }, [state?.activeRun?.jobRunUuid, state?.lastRun?.jobRunUuid, onRunStateChange]);

  // Spinner while the POST is in flight, while the success banner is still showing
  // (before the first poll confirms the active run), and while the job is running on the server.
  // This prevents the brief re-enable flash between POST completion and the first poll response.
  const isSyncing =
    syncState.status === 'submitting' || syncState.status === 'success' || hasActiveRun;

  // Re-fetch when the parent signals that settings were saved (refreshKey incremented)
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void refresh(true);
  }, [refreshKey, refresh]);

  async function handleTriggerSync() {
    if (!state) return;
    const enabledResources = state.resources.filter((r) => r.enabled);
    const toSync = enabledResources.length > 0 ? enabledResources : state.resources.slice(0, 1);
    if (toSync.length === 0) return;

    // Trigger all enabled resources so both build and release pipelines (or all repos/boards) sync.
    let anySuccess = false;
    for (const resource of toSync) {
      const result = await trigger(resource.resourceUuid);
      if (result) anySuccess = true;
    }
    if (anySuccess) {
      // Open the Sync Log right pane immediately so the user can watch progress.
      setRightPaneMode('sync-log');
      setRightPaneVisible(true);
      void refresh(true);
    }
  }

  const sourceLabel = SOURCE_TYPE_LABELS[sourceType];

  if (loading) {
    return (
      <div className="ss-panel ss-panel--loading" aria-busy="true">
        <p>Loading {sourceLabel}…</p>
      </div>
    );
  }

  if (error) {
    return <PageErrorState message={`Failed to load: ${error}`} onRetry={() => void refresh()} />;
  }

  return (
    <div
      className={`ss-panel${sourceType === 'repo' || sourceType === 'pipeline' ? ' ss-panel--fill' : ''}`}
    >
      <header className="ss-panel__header">
        <div className="ss-panel__header-left">
          <p className="ss-panel__breadcrumb">{breadcrumb}</p>
          <h1 className="ss-panel__title">{sourceLabel}</h1>
        </div>

        <div className="ss-panel__header-actions">
          {canEdit && (
            <Tooltip.Simple label="Trigger manual sync">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void handleTriggerSync()}
                disabled={isSyncing || !state?.resources.length}
                aria-label="Trigger manual sync"
              >
                {isSyncing && <RefreshIcon spinning={true} />}
                {isSyncing ? 'Syncing…' : 'Sync now'}
              </Button>
            </Tooltip.Simple>
          )}
        </div>
      </header>

      {syncState.status === 'error' && (
        <div className="ss-panel__banner ss-panel__banner--error" role="alert">
          Trigger failed: {syncState.message}
        </div>
      )}

      {sourceType === 'repo' ? (
        <SourceSystemRepoBrowser
          projectUuid={projectUuid}
          refreshKey={state?.lastRun?.syncRunUuid ?? null}
          onSetDraftExplicitContext={onSetDraftExplicitContext}
        />
      ) : sourceType === 'pipeline' ? (
        <PipelineBrowser
          projectUuid={projectUuid}
          state={state}
          refreshKey={state?.lastRun?.syncRunUuid ?? null}
        />
      ) : (
        <>
          <SourceSystemDataSection state={state} />
          <SourceSystemLogsSection projectUuid={projectUuid} state={state} />
        </>
      )}
    </div>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <ReloadIcon
      width={IconSize.sm}
      height={IconSize.sm}
      aria-hidden="true"
      className={spinning ? 'ss-panel__action-icon--spinning' : undefined}
    />
  );
}
